import { UserData } from 'auth/context/AuthContext';
import { Roles } from '../firebase/interfaces';
import { useGetFhirProviderbyEmailLazyQuery } from 'graphql/generated/remote-schema-hasura';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useEffect, useCallback, useState } from 'react';
import { ProviderProfileDataOptional } from 'app/my-account/interfaces/profile.interfaces';

import StorageService from '../utilities/storageService';
import { HASURA_USER_STORAGE_KEY } from '../firebase/constants';

const hasuraUserStorage = new StorageService(HASURA_USER_STORAGE_KEY);

const useFhirProviderDataLoader = (
  loggedUser: UserData | null,
  email: string | undefined,
) => {
  const { email: loggedUserEmail } = hasuraUserStorage.getData() || {};
  const [providerProfileData, setProviderProfileData] =
    useState<ProviderProfileDataOptional | null>(null);
  const [initialProviderProfileData, setInitialProviderProfileData] =
    useState<ProviderProfileDataOptional | null>(null);
  const [updateCount, setUpdateCount] = useState(0);

  const updater = () => setUpdateCount((up) => up + 1);

  const getMfaParts = (mfaPhoneNumber?: string) => {
    try {
      const parsedPhoneNumber = parsePhoneNumber(mfaPhoneNumber || '');
      return {
        mfaPhone: parsedPhoneNumber.nationalNumber,
        mfaCountry: parsedPhoneNumber.countryCallingCode,
      };
    } catch (error) {
      console.error('Unable to parse phone number');
      return {
        mfaPhone: '',
        mfaCountry: '',
      };
    }
  };
  const [fhirGetProviderDataByEmail, { loading: fhirProviderDataLoading }] =
    useGetFhirProviderbyEmailLazyQuery({});

  const fetchData = useCallback(async () => {
    try {
      const { data } = await fhirGetProviderDataByEmail({
        nextFetchPolicy: 'network-only',
        variables: { email: email || loggedUserEmail },
      });
      if (
        loggedUser?.role === Roles.PROVIDER &&
        data &&
        data?.getFHIRProviderbyEmail.providers.length > 0
      ) {
        const user = data.getFHIRProviderbyEmail.providers[0];

        const education = (user?.SENSITIVE_education || []).map(
          (education) => ({
            medicalSchool: education?.medicalSchool,
            residency: education?.residency,
            undergraduate: education?.undergraduate,
            isStillResident: education?.isStillResident,
            residencyStartDate: education?.residencyStartDate,
            residencyEndDate: education?.residencyEndDate,
          }),
        );

        const provider = {
          firstname: user?.SENSITIVE_firstname || '',
          lastname: user?.SENSITIVE_lastname || '',
          email: user?.SENSITIVE_email || '',
          gender: user?.SENSITIVE_gender || '',
          genderDescribe: user?.SENSITIVE_gender_describe || '',
          phone: user?.SENSITIVE_phone || '',
          nationalProviderIdentifier:
            user?.SENSITIVE_national_provider_identifier || '',
          languagesOfCommunication:
            user?.SENSITIVE_languages_of_communication || '',
          credentials: {
            titles: user?.SENSITIVE_credentials.titles,
            certifications: user?.SENSITIVE_credentials.certifications,
            medicalRole: user?.SENSITIVE_credentials.medicalRole,
            yearsOfPractice: user?.SENSITIVE_credentials.yearsOfPractice,
          },
          about: user?.SENSITIVE_about || '',
          stateLicenses: user?.SENSITIVE_credentials?.stateLicenses || [],
          education: education || '',
          office_information: {
            officeName: user?.SENSITIVE_office_information?.officeName || '',
            email: user?.SENSITIVE_office_information?.email || '',
            phone: user?.SENSITIVE_office_information?.phone || '',
            address1: user?.SENSITIVE_office_information?.address1 || '',
            address2: user?.SENSITIVE_office_information?.address2 || '',
            country: user?.SENSITIVE_office_information?.country || '',
            state: user?.SENSITIVE_office_information?.state || '',
            city: user?.SENSITIVE_office_information?.city || '',
            zipCode: user?.SENSITIVE_office_information?.zipCode || '',
          },
          address_information: {
            address1: user?.SENSITIVE_address_information?.address1 || '',
            address2: user?.SENSITIVE_address_information?.address2 || '',
            country: user?.SENSITIVE_address_information?.country || '',
            state: user?.SENSITIVE_address_information?.state || '',
            city: user?.SENSITIVE_address_information?.city || '',
            zipCode: user?.SENSITIVE_address_information?.zipCode || '',
          },
          dateOfBirth: user.SENSITIVE_dob || '',
          providerBalance: user?.providerBalance || null,
          signInProvider: loggedUser?.signInProvider || null,
          ...getMfaParts(loggedUser?.mfaPhoneNumber),
          acuityCalendarId: user?.acuityCalendarId || '',
          serviceOfferings: user?.serviceOfferings || [],
          isStripeConnected: user?.isStripeConnected || undefined,
        };
        setProviderProfileData(provider);
        setInitialProviderProfileData(provider);
      }
    } catch (error) {
      console.error('Error fetching FHIR provider data:', error);
    }
  }, [
    email,
    loggedUserEmail,
    fhirGetProviderDataByEmail,
    loggedUser?.mfaPhoneNumber,
    loggedUser?.role,
    loggedUser?.signInProvider,
    setProviderProfileData,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, updateCount]);

  return {
    fhirProviderDataLoading,
    providerProfileData,
    initialProviderProfileData,
    setProviderProfileData,
    updater,
  };
};

export default useFhirProviderDataLoader;
